import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { toast } from "react-toastify";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  Copy,
  Eye,
  Link as LinkIcon,
  MessageSquare,
  Paperclip,
  Pencil,
  Plus,
  RefreshCcw,
  Send,
  X
} from "lucide-react";

import ConfirmationModal from "../../components/ConfirmationModal";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SocketContext } from "../../context/Socket/SocketContext";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import toastError from "../../errors/toastError";
import api from "../../services/api";

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    overflowY: "auto",
    borderRadius: 16,
    border: "1px solid #f3f4f6",
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.08)",
    background: "#ffffff",
    ...theme.scrollbarStyles
  },
  tableHeadCell: {
    background: "#f8fafc",
    color: "#334155",
    fontSize: 12,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    borderBottom: "1px solid #e5e7eb",
    whiteSpace: "nowrap"
  },
  tableBodyCell: {
    borderBottom: "1px solid #f1f5f9",
    fontSize: 14,
    color: "#1f2937"
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    flexWrap: "wrap"
  },
  filters: {
    display: "grid",
    gridTemplateColumns: "minmax(220px, 320px) minmax(220px, 1fr)",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr"
    }
  },
  actionsStack: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap"
  },
  iconButton: {
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    padding: 8,
    background: "#ffffff",
    "&:hover": {
      background: "#f8fafc"
    }
  },
  dialogContent: {
    display: "grid",
    gap: theme.spacing(2),
    minWidth: 420,
    [theme.breakpoints.down("xs")]: {
      minWidth: "auto"
    }
  },
  detailsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(120px, 1fr))",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr 1fr"
    }
  },
  infoBox: {
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    padding: theme.spacing(1.5),
    background: "#ffffff"
  },
  label: {
    color: "#64748b",
    fontSize: 12,
    textTransform: "uppercase",
    fontWeight: 700,
    letterSpacing: "0.04em"
  },
  value: {
    color: "#0f172a",
    fontSize: 14,
    fontWeight: 600,
    marginTop: 4,
    overflowWrap: "anywhere"
  },
  tabPanel: {
    paddingTop: theme.spacing(2)
  },
  messageBox: {
    display: "grid",
    gap: theme.spacing(1.5)
  },
  attachmentBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    padding: theme.spacing(1.25),
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    background: "#f8fafc",
    minHeight: 48,
    [theme.breakpoints.down("xs")]: {
      alignItems: "flex-start",
      flexDirection: "column"
    }
  },
  attachmentInfo: {
    minWidth: 0,
    color: "#334155",
    fontSize: 13
  },
  attachmentName: {
    color: "#0f172a",
    fontWeight: 700,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: 520
  },
  attachmentActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexShrink: 0
  }
}));

const allowedGroupMediaTypes = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "video/mp4",
  "audio/mpeg",
  "audio/mp3",
  "audio/ogg",
  "audio/mp4"
];

const groupMediaAccept = allowedGroupMediaTypes.join(",");
const groupMediaMaxSize = 40 * 1024 * 1024;

const formatFileSize = size => {
  if (!Number.isFinite(size)) return "";
  if (size < 1024 * 1024) return `${Math.max(1, Math.round(size / 1024))} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const statusMap = {
  active: "Ativo",
  unavailable: "Indisponivel",
  not_synced: "Nao sincronizado",
  setting_up: "Configuracao pendente",
  setup_failed: "Falha na configuracao",
  connection_offline: "Conexao offline"
};

const statusClass = {
  active: "bg-emerald-100 text-emerald-700",
  unavailable: "bg-red-100 text-red-700",
  not_synced: "bg-amber-100 text-amber-700",
  setting_up: "bg-sky-100 text-sky-700",
  setup_failed: "bg-red-100 text-red-700",
  connection_offline: "bg-orange-100 text-orange-700"
};

const isGroupReady = group =>
  group?.status === "active" && group?.isAnnouncement === true;

const maskJid = value => {
  if (!value) return "-";
  return String(value).replace(/\d{8,}/g, number => {
    if (number.length <= 9) {
      return `${number.slice(0, 3)}*****${number.slice(-2)}`;
    }
    return `${number.slice(0, 5)}*****${number.slice(-4)}`;
  });
};

const copyToClipboard = async text => {
  if (!text) return;

  if (navigator.clipboard) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const element = document.createElement("textarea");
  element.value = text;
  document.body.appendChild(element);
  element.select();
  document.execCommand("copy");
  document.body.removeChild(element);
};

const WhatsappGroups = () => {
  const classes = useStyles();
  const socketManager = useContext(SocketContext);
  const { user } = useContext(AuthContext);
  const { whatsApps, loading: loadingWhatsApps } = useContext(WhatsAppsContext);

  const [selectedWhatsappId, setSelectedWhatsappId] = useState("");
  const [searchParam, setSearchParam] = useState("");
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [inviteLink, setInviteLink] = useState("");
  const [participants, setParticipants] = useState([]);
  const [detailsTab, setDetailsTab] = useState(0);
  const [message, setMessage] = useState("");
  const [media, setMedia] = useState(null);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [form, setForm] = useState({
    name: "",
    description: ""
  });
  const [confirmRevokeOpen, setConfirmRevokeOpen] = useState(false);
  const mediaInputRef = useRef(null);

  const connectedWhatsApps = useMemo(() => whatsApps || [], [whatsApps]);
  const selectedWhatsapp = connectedWhatsApps.find(
    whatsapp => Number(whatsapp.id) === Number(selectedWhatsappId)
  );
  const selectedGroupReady = isGroupReady(selectedGroup);

  useEffect(() => {
    if (!selectedWhatsappId && connectedWhatsApps.length) {
      setSelectedWhatsappId(String(connectedWhatsApps[0].id));
    }
  }, [connectedWhatsApps, selectedWhatsappId]);

  const fetchGroups = useCallback(async () => {
    if (!selectedWhatsappId) {
      setGroups([]);
      return;
    }

    setLoading(true);

    try {
      const { data } = await api.get("/whatsapp-groups", {
        params: {
          whatsappId: selectedWhatsappId,
          searchParam
        }
      });
      setGroups(data.groups || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [searchParam, selectedWhatsappId]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchGroups();
    }, 300);

    return () => clearTimeout(timeout);
  }, [fetchGroups]);

  useEffect(() => {
    const companyId = user?.companyId || localStorage.getItem("companyId");
    if (!companyId) return undefined;

    const socket = socketManager.getSocket(companyId);
    const eventName = `company-${companyId}-whatsappGroup`;
    const handler = data => {
      if (
        data.action === "update" &&
        Number(data.group?.whatsappId) === Number(selectedWhatsappId)
      ) {
        fetchGroups();
      }
    };

    socket.on(eventName, handler);

    return () => {
      socket.off(eventName, handler);
    };
  }, [fetchGroups, selectedWhatsappId, socketManager, user?.companyId]);

  const resetForm = () => {
    setForm({
      name: "",
      description: ""
    });
  };

  const handleOpenCreate = () => {
    setSelectedGroup(null);
    resetForm();
    setGroupModalOpen(true);
  };

  const handleOpenEdit = group => {
    setSelectedGroup(group);
    setForm({
      name: group.name || "",
      description: group.description || ""
    });
    setGroupModalOpen(true);
  };

  const handleSaveGroup = async () => {
    if (!selectedWhatsappId) {
      toast.warn("Selecione uma conexao WhatsApp.");
      return;
    }

    try {
      if (selectedGroup) {
        const { data } = await api.put(`/whatsapp-groups/${selectedGroup.id}`, {
          whatsappId: selectedWhatsappId,
          ...form
        });
        toast.success("Grupo atualizado.");
        setSelectedGroup(data);
      } else {
        const { data } = await api.post("/whatsapp-groups", {
          whatsappId: selectedWhatsappId,
          ...form
        });
        if (isGroupReady(data.group)) {
          toast.success("Grupo criado.");
        } else {
          toast.warn("Grupo criado, mas a configuracao ainda nao foi concluida.");
        }
        setInviteLink(data.inviteLink || "");
        setSelectedGroup(data.group);
        setDetailsOpen(true);
      }

      setGroupModalOpen(false);
      resetForm();
      fetchGroups();
    } catch (err) {
      if (err?.response?.data?.error === "GROUP_SETUP_FAILED") {
        toast.warn(
          "Grupo criado no WhatsApp, mas a configuracao obrigatoria falhou. Nao clique em criar novamente; sincronize o grupo na listagem."
        );
        setGroupModalOpen(false);
        resetForm();
        fetchGroups();
        return;
      }

      toastError(err);
    }
  };

  const handleOpenDetails = async group => {
    setSelectedGroup(group);
    setDetailsTab(0);
    setInviteLink("");
    setParticipants([]);
    setMessage("");
    setMedia(null);
    if (mediaInputRef.current) {
      mediaInputRef.current.value = "";
    }
    setDetailsOpen(true);
  };

  const handleSyncGroup = async group => {
    try {
      const { data } = await api.post(`/whatsapp-groups/${group.id}/sync`, {
        whatsappId: selectedWhatsappId
      });
      toast.success("Grupo sincronizado.");
      setGroups(prev =>
        prev.map(item => (Number(item.id) === Number(data.id) ? data : item))
      );
      if (selectedGroup?.id === data.id) {
        setSelectedGroup(data);
      }
    } catch (err) {
      toastError(err);
    }
  };

  const handleSyncAll = async () => {
    for (const group of groups) {
      await handleSyncGroup(group);
    }
  };

  const handleLoadInvite = async (group = selectedGroup) => {
    if (!group) return "";
    if (!isGroupReady(group)) {
      toast.warn("Conclua a configuracao do grupo antes de gerar convite.");
      return "";
    }

    try {
      const { data } = await api.get(
        `/whatsapp-groups/${group.id}/invite`,
        {
          params: {
            whatsappId: selectedWhatsappId
          }
        }
      );
      setInviteLink(data.inviteLink || "");
      return data.inviteLink || "";
    } catch (err) {
      toastError(err);
      return "";
    }
  };

  const handleCopyInvite = async () => {
    const link = inviteLink || (await handleLoadInvite());

    if (!link) return;

    await copyToClipboard(link);
    toast.success("Link copiado.");
  };

  const handleRevokeInvite = async () => {
    if (!selectedGroup) return;
    if (!selectedGroupReady) {
      toast.warn("Conclua a configuracao do grupo antes de gerar novo convite.");
      return;
    }

    try {
      const { data } = await api.post(
        `/whatsapp-groups/${selectedGroup.id}/invite/revoke`,
        {
          whatsappId: selectedWhatsappId
        }
      );
      setInviteLink(data.inviteLink || "");
      toast.success("Novo link gerado.");
    } catch (err) {
      toastError(err);
    }
  };

  const handleLoadParticipants = async () => {
    if (!selectedGroup) return;

    try {
      const { data } = await api.get(
        `/whatsapp-groups/${selectedGroup.id}/participants`,
        {
          params: {
            whatsappId: selectedWhatsappId
          }
        }
      );
      setParticipants(data || []);
    } catch (err) {
      toastError(err);
    }
  };

  const handleChangeTab = (_, value) => {
    setDetailsTab(value);

    if (value === 1 && participants.length === 0) {
      handleLoadParticipants();
    }
  };

  const handleSelectMedia = event => {
    const file = event.target.files?.[0];

    if (!file) return;

    if (!allowedGroupMediaTypes.includes(file.type)) {
      toast.warn("Formato nao permitido. Use imagem, video MP4 ou audio.");
      event.target.value = "";
      return;
    }

    if (file.size > groupMediaMaxSize) {
      toast.warn("Arquivo excede o limite de 40 MB.");
      event.target.value = "";
      return;
    }

    setMedia(file);
  };

  const handleRemoveMedia = () => {
    setMedia(null);
    if (mediaInputRef.current) {
      mediaInputRef.current.value = "";
    }
  };

  const handleSendMessage = async () => {
    if (!selectedGroup) return;
    if (!selectedGroupReady) {
      toast.warn("Conclua a configuracao do grupo antes de enviar comunicado.");
      return;
    }

    if (!message.trim() && !media) {
      toast.warn("Informe uma mensagem ou selecione um anexo.");
      return;
    }

    setSendingMessage(true);

    try {
      if (media) {
        const formData = new FormData();
        formData.append("whatsappId", selectedWhatsappId);
        formData.append("message", message);
        formData.append("media", media);

        await api.post(
          `/whatsapp-groups/${selectedGroup.id}/messages`,
          formData
        );
      } else {
        await api.post(`/whatsapp-groups/${selectedGroup.id}/messages`, {
          whatsappId: selectedWhatsappId,
          message
        });
      }

      setMessage("");
      handleRemoveMedia();
      toast.success("Comunicado enviado.");
    } catch (err) {
      toastError(err);
    } finally {
      setSendingMessage(false);
    }
  };

  const renderStatus = status => {
    const badgeClass = statusClass[status] || "bg-gray-100 text-gray-700";
    return (
      <span className={`badge ${badgeClass}`}>
        {statusMap[status] || status || "-"}
      </span>
    );
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title="Gerar novo link"
        open={confirmRevokeOpen}
        onClose={setConfirmRevokeOpen}
        onConfirm={handleRevokeInvite}
      >
        O link anterior deixara de funcionar para novas entradas no grupo.
      </ConfirmationModal>

      <Dialog
        open={groupModalOpen}
        onClose={() => setGroupModalOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{selectedGroup ? "Editar grupo" : "Novo grupo"}</DialogTitle>
        <DialogContent dividers className={classes.dialogContent}>
          <FormControl variant="outlined" fullWidth>
            <InputLabel>Conexao WhatsApp</InputLabel>
            <Select
              value={selectedWhatsappId}
              onChange={event => setSelectedWhatsappId(event.target.value)}
              label="Conexao WhatsApp"
              disabled={Boolean(selectedGroup)}
            >
              {connectedWhatsApps.map(whatsapp => (
                <MenuItem key={whatsapp.id} value={String(whatsapp.id)}>
                  {whatsapp.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Nome do grupo"
            variant="outlined"
            value={form.name}
            onChange={event =>
              setForm(prev => ({ ...prev, name: event.target.value }))
            }
            fullWidth
            required
          />
          <TextField
            label="Descricao"
            variant="outlined"
            value={form.description}
            onChange={event =>
              setForm(prev => ({ ...prev, description: event.target.value }))
            }
            fullWidth
            multiline
            minRows={3}
          />
          <div className={classes.infoBox}>
            <div className={classes.label}>Tipo</div>
            <div className={classes.value}>Grupo de comunicados</div>
            <p className="mt-1 text-sm text-gray-500">
              Somente administradores poderao enviar mensagens. Participantes
              entram pelo link de convite.
            </p>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGroupModalOpen(false)}>Cancelar</Button>
          <Button
            color="primary"
            variant="contained"
            onClick={handleSaveGroup}
            disabled={!form.name.trim() || !selectedWhatsappId}
          >
            {selectedGroup ? "Salvar" : "Criar grupo"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{selectedGroup?.name || "Grupo"}</DialogTitle>
        <DialogContent dividers>
          <div className={classes.detailsGrid}>
            <div className={classes.infoBox}>
              <div className={classes.label}>Conexao</div>
              <div className={classes.value}>{selectedWhatsapp?.name || "-"}</div>
            </div>
            <div className={classes.infoBox}>
              <div className={classes.label}>Tipo</div>
              <div className={classes.value}>Somente admins</div>
            </div>
            <div className={classes.infoBox}>
              <div className={classes.label}>Participantes</div>
              <div className={classes.value}>
                {selectedGroup?.participantsCount ?? "-"}
              </div>
            </div>
            <div className={classes.infoBox}>
              <div className={classes.label}>Status</div>
              <div className={classes.value}>{renderStatus(selectedGroup?.status)}</div>
            </div>
          </div>

          {!selectedGroupReady && selectedGroup && (
            <div className={`${classes.infoBox} mb-3`}>
              <div className={classes.label}>Configuracao</div>
              <div className={classes.value}>
                Convite e envio ficam bloqueados ate o grupo estar como somente admins.
              </div>
            </div>
          )}

          <div className="mb-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="btn-secondary inline-flex items-center gap-2"
              onClick={handleCopyInvite}
              disabled={!selectedGroupReady}
            >
              <Copy size={16} />
              Copiar convite
            </button>
            <button
              type="button"
              className="btn-secondary inline-flex items-center gap-2"
              onClick={() => setConfirmRevokeOpen(true)}
              disabled={!selectedGroupReady}
            >
              <LinkIcon size={16} />
              Gerar novo convite
            </button>
            <button
              type="button"
              className="btn-secondary inline-flex items-center gap-2"
              onClick={() => selectedGroup && handleSyncGroup(selectedGroup)}
            >
              <RefreshCcw size={16} />
              Sincronizar
            </button>
          </div>

          <Tabs value={detailsTab} onChange={handleChangeTab}>
            <Tab label="Informacoes" />
            <Tab label="Participantes" />
            <Tab label="Mensagens" />
          </Tabs>

          {detailsTab === 0 && (
            <div className={classes.tabPanel}>
              <div className={classes.infoBox}>
                <div className={classes.label}>Group JID</div>
                <div className={classes.value}>{maskJid(selectedGroup?.groupJid)}</div>
              </div>
              <div className={`${classes.infoBox} mt-3`}>
                <div className={classes.label}>Descricao</div>
                <div className={classes.value}>
                  {selectedGroup?.description || "-"}
                </div>
              </div>
              {inviteLink && (
                <div className={`${classes.infoBox} mt-3`}>
                  <div className={classes.label}>Convite</div>
                  <div className={classes.value}>{inviteLink}</div>
                </div>
              )}
            </div>
          )}

          {detailsTab === 1 && (
            <div className={classes.tabPanel}>
              <Paper variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.tableHeadCell}>Nome</TableCell>
                      <TableCell className={classes.tableHeadCell}>JID</TableCell>
                      <TableCell className={classes.tableHeadCell}>Papel</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {participants.map(participant => (
                      <TableRow key={participant.id}>
                        <TableCell className={classes.tableBodyCell}>
                          {participant.displayName || "-"}
                        </TableCell>
                        <TableCell className={classes.tableBodyCell}>
                          {participant.maskedJid}
                        </TableCell>
                        <TableCell className={classes.tableBodyCell}>
                          {participant.isAdmin ? "Administrador" : "Participante"}
                        </TableCell>
                      </TableRow>
                    ))}
                    {participants.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3} align="center">
                          Nenhum participante carregado.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Paper>
            </div>
          )}

          {detailsTab === 2 && (
            <div className={`${classes.tabPanel} ${classes.messageBox}`}>
              <TextField
                label="Mensagem institucional"
                variant="outlined"
                value={message}
                onChange={event => setMessage(event.target.value)}
                multiline
                minRows={4}
                fullWidth
              />
              <input
                ref={mediaInputRef}
                type="file"
                accept={groupMediaAccept}
                onChange={handleSelectMedia}
                style={{ display: "none" }}
              />
              <div className={classes.attachmentBar}>
                {media ? (
                  <div className={classes.attachmentInfo}>
                    <div className={classes.attachmentName}>{media.name}</div>
                    <div>
                      {media.type || "Tipo desconhecido"} · {formatFileSize(media.size)}
                    </div>
                  </div>
                ) : (
                  <div className={classes.attachmentInfo}>
                    Nenhum anexo selecionado
                  </div>
                )}
                <div className={classes.attachmentActions}>
                  <Tooltip title={media ? "Trocar anexo" : "Anexar arquivo"}>
                    <button
                      type="button"
                      className="btn-secondary inline-flex items-center gap-2"
                      onClick={() => mediaInputRef.current?.click()}
                      disabled={!selectedGroupReady || sendingMessage}
                    >
                      <Paperclip size={16} />
                      Anexar
                    </button>
                  </Tooltip>
                  {media && (
                    <Tooltip title="Remover anexo">
                      <button
                        type="button"
                        className={classes.iconButton}
                        onClick={handleRemoveMedia}
                        disabled={sendingMessage}
                      >
                        <X size={16} />
                      </button>
                    </Tooltip>
                  )}
                </div>
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  className="btn-primary inline-flex items-center gap-2"
                  onClick={handleSendMessage}
                  disabled={
                    (!message.trim() && !media) ||
                    !selectedGroupReady ||
                    sendingMessage
                  }
                >
                  <Send size={16} />
                  {sendingMessage ? "Enviando..." : "Enviar comunicado"}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <div className="ds-card mb-4">
        <MainHeader>
          <div>
            <h1 className="page-title">Grupos do WhatsApp</h1>
            <p className="mt-1 text-sm text-gray-500">
              Gerencie grupos institucionais de comunicados por conexao.
            </p>
          </div>

          <MainHeaderButtonsWrapper>
            <div className={classes.headerActions}>
              <button
                type="button"
                className="btn-secondary inline-flex items-center gap-2"
                onClick={handleSyncAll}
                disabled={!selectedWhatsappId || groups.length === 0}
              >
                <RefreshCcw size={16} />
                Sincronizar
              </button>
              <button
                type="button"
                className="btn-primary inline-flex items-center gap-2"
                onClick={handleOpenCreate}
                disabled={!selectedWhatsappId}
              >
                <Plus size={16} />
                Criar grupo
              </button>
            </div>
          </MainHeaderButtonsWrapper>
        </MainHeader>
      </div>

      <div className={classes.filters}>
        <FormControl variant="outlined" fullWidth disabled={loadingWhatsApps}>
          <InputLabel>Conexao</InputLabel>
          <Select
            value={selectedWhatsappId}
            onChange={event => setSelectedWhatsappId(event.target.value)}
            label="Conexao"
          >
            {connectedWhatsApps.map(whatsapp => (
              <MenuItem key={whatsapp.id} value={String(whatsapp.id)}>
                {whatsapp.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          variant="outlined"
          label="Pesquisar"
          value={searchParam}
          onChange={event => setSearchParam(event.target.value)}
          fullWidth
        />
      </div>

      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center" className={classes.tableHeadCell}>
                Nome
              </TableCell>
              <TableCell align="center" className={classes.tableHeadCell}>
                Participantes
              </TableCell>
              <TableCell align="center" className={classes.tableHeadCell}>
                Tipo
              </TableCell>
              <TableCell align="center" className={classes.tableHeadCell}>
                Status
              </TableCell>
              <TableCell align="center" className={classes.tableHeadCell}>
                Acoes
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRowSkeleton columns={5} />
            ) : (
              <>
                {groups.map(group => {
                  const groupReady = isGroupReady(group);

                  return (
                    <TableRow key={group.id}>
                      <TableCell align="center" className={classes.tableBodyCell}>
                        <span className="font-semibold text-slate-800">
                          {group.name}
                        </span>
                      </TableCell>
                      <TableCell align="center" className={classes.tableBodyCell}>
                        {group.participantsCount ?? "-"}
                      </TableCell>
                      <TableCell align="center" className={classes.tableBodyCell}>
                        Comunicados
                      </TableCell>
                      <TableCell align="center" className={classes.tableBodyCell}>
                        {renderStatus(group.status)}
                      </TableCell>
                      <TableCell align="center" className={classes.tableBodyCell}>
                        <div className={classes.actionsStack}>
                          <Tooltip title="Visualizar">
                            <button
                              type="button"
                              className={classes.iconButton}
                              onClick={() => handleOpenDetails(group)}
                            >
                              <Eye size={16} />
                            </button>
                          </Tooltip>
                          <Tooltip title="Editar">
                            <button
                              type="button"
                              className={classes.iconButton}
                              onClick={() => handleOpenEdit(group)}
                            >
                              <Pencil size={16} />
                            </button>
                          </Tooltip>
                          <Tooltip title="Convite">
                            <button
                              type="button"
                              className={classes.iconButton}
                              disabled={!groupReady}
                              onClick={() => {
                                handleOpenDetails(group);
                                handleLoadInvite(group);
                              }}
                            >
                              <LinkIcon size={16} />
                            </button>
                          </Tooltip>
                          <Tooltip title="Sincronizar">
                            <button
                              type="button"
                              className={classes.iconButton}
                              onClick={() => handleSyncGroup(group)}
                            >
                              <RefreshCcw size={16} />
                            </button>
                          </Tooltip>
                          <Tooltip title="Mensagem">
                            <button
                              type="button"
                              className={classes.iconButton}
                              disabled={!groupReady}
                              onClick={() => {
                                handleOpenDetails(group);
                                setDetailsTab(2);
                              }}
                            >
                              <MessageSquare size={16} />
                            </button>
                          </Tooltip>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {groups.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      Nenhum grupo encontrado para a conexao selecionada.
                    </TableCell>
                  </TableRow>
                )}
              </>
            )}
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default WhatsappGroups;
